import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { ElectronicModel } from '@/types';
import { useCreateElectronicModelMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { SearchElectronicBrands } from '@/pages/ElectronicBrands/components';

export interface CreateElectronicModelProps extends DialogProps {
  onClose(): void;
}

const CreateElectronicModel: React.FC<CreateElectronicModelProps> = ({ onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [createElectronicModel, createElectronicModelResponse] = useCreateElectronicModelMutation();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange } = useFormik<ElectronicModel>({
    initialValues: {
      name: '',
      electronic_brand: null,
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      electronic_brand: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
    }),
    async onSubmit(electronicModel, { resetForm }) {
      try {
        await createElectronicModel(electronicModel).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Добавить модель электроники</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={createElectronicModelResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <SearchElectronicBrands
                value={values.electronic_brand}
                onChange={(event, option) => setFieldValue('electronic_brand', option)}
                disabled={createElectronicModelResponse.isLoading}
                getOptionLabel={(option) => option.name}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    name="electronic_brand"
                    label="Бренд электроники"
                    placeholder="Выберите из списка"
                    error={Boolean(touched.electronic_brand && errors.electronic_brand)}
                    helperText={touched.electronic_brand && errors.electronic_brand}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={createElectronicModelResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={createElectronicModelResponse.isLoading}>
            Добавить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateElectronicModel;
